import { gql, useQuery } from "@apollo/client";
import { Dialog, Disclosure, Transition } from "@headlessui/react";
import { ChevronUpIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { animated as a, useTrail } from "@react-spring/web";
import { Fragment, useContext, useMemo } from "react";
import type { LinkProps } from "react-router-dom";
import {
  Link,
  useLocation,
  useNavigate,
  useResolvedPath,
} from "react-router-dom";

import LogoLight from "../assets/logo-light.svg";
import { Avatar } from "../components/appearance";
import { GenContext } from "../contexts/GenContext";
import { pages } from "../modules";
import { isAuthorizedForResource, isDeveloper, useAuth } from "../modules/auth";
import { AuthShield } from "../modules/auth/core";
import { classNames } from "../utils";

interface NavigationProps {
  name: string;
  path: string;
  icon?: JSX.Element | string;
  access?: string[] | undefined;
  children?: {
    name: string;
    path: string;
    access?: string[] | undefined;
  }[];
}

const navigation: NavigationProps[] = pages;

interface NavigationLinkProps extends LinkProps {
  name: string;
  icon?: JSX.Element | string;
  access?: string[] | undefined;
  child?: { name: string; path: string; access?: string[] | undefined }[];
}

interface SubLinkProps {
  open: boolean;
  name: string;
  icon?: JSX.Element | string;
  access?: string[] | undefined;
  child: { name: string; path: string; access?: string[] | undefined }[];
  active: boolean;
}

const FETCH_ORDERS = gql`
  query FetchOrders(
    $searchQuery: String!
    $searchFilter: [String!]
    $status: Int
  ) {
    fetchOrders(
      searchQuery: $searchQuery
      searchFilter: $searchFilter
      status: $status
    ) {
      id
    }
  }
`;

export default function NavLayout() {
  const { currentUser, currentRole } = useAuth();
  const { sidebarOpen, handleMenu } = useContext(GenContext);
  const location = useLocation();
  const navigate = useNavigate();

  const secondaryNavigation = useMemo<NavigationProps[]>(() => {
    const menus: NavigationProps[] = [
      { name: "Settings", path: "/settings", icon: "bi bi-gear" },
    ];
    if (isDeveloper(currentRole)) {
      menus.push({ name: "Store", path: "/store", icon: "bi bi-sliders" });
    }
    menus.push({
      name: "Logout",
      path: "/logout",
      icon: "bi bi-box-arrow-left",
    });
    return menus;
  }, [currentRole]);

  const { data: openOrders } = useQuery(FETCH_ORDERS, {
    variables: {
      searchQuery: "",
      searchFilter: ["status"],
      status: 2,
    },
  });

  const { data: shipOrders } = useQuery(FETCH_ORDERS, {
    variables: {
      searchQuery: "",
      searchFilter: ["status"],
      status: 4,
    },
  });

  function MenuItem({
    children,
    to,
    access,
    name,
    icon,
    child = [],
    ...props
  }: NavigationLinkProps) {
    let resolved = useResolvedPath(to);
    let match = location.pathname.match(resolved.pathname);
    let active = match ? true : false;
    if (!isAuthorizedForResource(currentRole, access)) return null;

    return child.length ? (
      <Disclosure as="div" defaultOpen={active} className="space-y-1">
        {(props) => (
          <MenuSubItem
            {...props}
            name={name}
            icon={icon}
            access={access}
            child={child}
            active={active}
          />
        )}
      </Disclosure>
    ) : (
      <div>
        <Link
          to={to}
          className={classNames(
            active
              ? " bg-primary-700/20 text-primary-700"
              : "text-primary-50 hover:text-primary-700",
            "group flex items-center rounded-md px-2 py-2 text-sm font-medium leading-6 transition-colors"
          )}
          aria-current={active ? "page" : undefined}
          {...props}
        >
          {typeof icon === "string" ? (
            <span
              className={classNames(
                active
                  ? " text-primary-700"
                  : "text-primary-50 group-hover:text-primary-700",
                "mr-4 h-6 w-6 flex-shrink-0 text-center text-xl leading-6 transition-colors",
                icon
              )}
              aria-hidden="true"
            />
          ) : (
            <span
              className={classNames(
                active
                  ? " text-primary-700"
                  : "text-primary-50 group-hover:text-primary-700",
                "mr-4 h-6 w-6 flex-shrink-0 text-center text-xl leading-6 transition-colors"
              )}
              aria-hidden="true"
            >
              {icon}
            </span>
          )}
          <span className="truncate">{name}</span>
        </Link>
      </div>
    );
  }

  function MenuSubItem({
    open,
    name,
    icon,
    access,
    child,
    active,
  }: SubLinkProps) {
    const trailConfig = {
      mass: 3,
      tension: active ? 0 : 1000,
      friction: 100,
    };

    const childTrail = useTrail(child ? child.length : 0, {
      config: trailConfig,
      opacity: open ? 1 : 0,
      x: open ? 0 : 10,
      height: open ? 36 : 0,
      from: { opacity: 0, x: 10, height: 0 },
    });

    if (!isAuthorizedForResource(currentRole, access)) return null;

    return (
      <>
        <Disclosure.Button
          className={classNames(
            active
              ? " bg-primary-700/20 text-primary-700"
              : "text-primary-50 hover:text-primary-700",
            "group flex w-full items-center rounded-md px-2 py-2 text-left text-sm font-medium leading-6 transition-colors"
          )}
        >
          {typeof icon === "string" ? (
            <span
              className={classNames(
                active
                  ? " text-primary-700"
                  : "text-primary-50 group-hover:text-primary-700",
                "mr-4 h-6 w-6 flex-shrink-0 text-center text-xl leading-6 transition-colors",
                icon
              )}
              aria-hidden="true"
            />
          ) : (
            <span
              className={classNames(
                active
                  ? " text-primary-700"
                  : "text-primary-50 group-hover:text-primary-700",
                "mr-4 h-6 w-6 flex-shrink-0 text-center text-xl leading-6 transition-colors"
              )}
              aria-hidden="true"
            >
              {icon}
            </span>
          )}
          <span className="flex-1 truncate">{name}</span>
          <ChevronUpIcon
            className={classNames(
              open ? "text-gray-400" : "rotate-90 text-gray-300",
              "ml-3 h-5 w-5 flex-shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-gray-400"
            )}
            aria-hidden="true"
          />
        </Disclosure.Button>
        <Disclosure.Panel className="space-y-1">
          {childTrail.map(({ x, height, ...rest }, index) => {
            let match = location.pathname.match(child[index].path);
            let active = match ? true : false;

            if (!isAuthorizedForResource(currentRole, child[index].access))
              return null;

            return (
              <a.div
                key={child[index].name}
                style={{
                  ...rest,
                  transform: x.to((x) => `translate3d(0,${x}px,0)`),
                }}
              >
                <a.div style={{ height }}>
                  <Link
                    to={child[index].path}
                    className={classNames(
                      active
                        ? "text-primary-300 hover:text-primary-400"
                        : "text-gray-300 hover:text-primary-700",
                      "group flex w-full items-center rounded-md py-2 pl-12 pr-2 text-sm font-normal transition-colors"
                    )}
                  >
                    <span className="truncate">{child[index].name}</span>
                  </Link>
                </a.div>
              </a.div>
            );
          })}
        </Disclosure.Panel>
      </>
    );
  }

  return (
    <>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 lg:hidden"
          onClose={handleMenu}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-[18rem] flex-1 flex-col bg-black pb-4 pt-5">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute right-0 top-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none"
                      onClick={() => handleMenu(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex flex-shrink-0 items-center px-4 py-6">
                  <Link to="/" className="w-3/4">
                    <span className="sr-only">
                      {process.env.REACT_APP_NAME}
                    </span>
                    <img src={LogoLight} alt={process.env.REACT_APP_NAME} />
                  </Link>
                </div>
                <nav
                  className="h-full flex-shrink-0 overflow-y-auto"
                  aria-label="Sidebar"
                >
                  <div className="flex flex-shrink-0 p-6">
                    <Link
                      to="/settings/account"
                      className="group block w-full flex-shrink-0"
                    >
                      <div className="flex items-center">
                        <div>
                          <Avatar
                            firstName={currentUser?.firstName}
                            lastName={currentUser?.lastName}
                            className="h-9 w-9 bg-primary-700 text-black"
                          />
                        </div>
                        <div className="ml-3">
                          <p className="truncate text-sm font-medium text-white">
                            {currentUser?.fullName}
                          </p>
                          <p className="text-xs font-medium text-primary-300 group-hover:text-gray-200">
                            {currentRole?.roleName}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </div>

                  <div className="space-y-1 px-4 pb-6">
                    <AuthShield access={["read-fleets"]}>
                      <div className="space-y-1">
                        <button
                          className="group flex w-full items-center rounded-md px-2 py-2 text-left text-sm font-medium leading-6 text-gray-100  transition-colors hover:text-primary-700"
                          id="headlessui-disclosure-button-:r2:"
                          type="button"
                          aria-expanded="false"
                        >
                          <span
                            className="bi bi-map mr-4 h-6 w-6 flex-shrink-0 text-center text-xl leading-6 text-primary-50 transition-colors group-hover:text-primary-700"
                            aria-hidden="true"
                          />
                          <span className="flex-1 truncate">
                            Track Shipment
                          </span>
                          <span className="ml-3 hidden rounded-full border border-solid border-primary-700 bg-primary-700 px-2.5 py-0.5 text-xs font-medium text-black transition-colors duration-150 ease-in-out group-hover:border-primary-600 group-hover:bg-primary-600 md:inline-block">
                            0
                          </span>
                        </button>
                      </div>
                    </AuthShield>
                    <AuthShield access={["read-fleets"]}>
                      <div>
                        <button
                          className="group flex w-full items-center rounded-md px-2 py-2 text-left text-sm font-medium leading-6 text-primary-50 transition-colors hover:text-primary-700"
                          type="button"
                          aria-expanded="false"
                        >
                          <span
                            className="mr-4 h-6 w-6 flex-shrink-0 text-center text-xl leading-6 text-primary-50 transition-colors group-hover:text-primary-700"
                            aria-hidden="true"
                          >
                            <svg
                              width="18"
                              height="10"
                              viewBox="0 0 18 10"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-full w-full"
                            >
                              <path
                                d="M3.25172 0.439941C3.08699 0.440611 2.9536 0.574005 2.95293 0.738871C2.9536 0.903603 3.08699 1.03699 3.25172 1.03766H11.5228V7.87216H7.74932C7.64472 7.15737 7.03199 6.60119 6.2943 6.60119C5.55675 6.60119 4.94403 7.15727 4.83942 7.87202L3.55279 7.87215V6.57197C3.55305 6.49202 3.52145 6.41514 3.46493 6.35862C3.40841 6.30211 3.33154 6.2705 3.25158 6.27077C3.17203 6.27104 3.09582 6.30304 3.03984 6.35943C2.98372 6.41594 2.95238 6.49242 2.95278 6.57197V8.17335C2.95345 8.33808 3.08685 8.47147 3.25158 8.47214H4.87397C5.04192 9.10574 5.61485 9.5796 6.29429 9.5796C6.97373 9.5796 7.54664 9.10591 7.71461 8.47214H13.1873C13.3552 9.10574 13.9282 9.5796 14.6076 9.5796C15.287 9.5796 15.8588 9.10591 16.0262 8.47214H17.3914C17.5562 8.47147 17.6896 8.33808 17.6902 8.17335V5.38709C17.6902 5.34866 17.6829 5.31035 17.6685 5.2746L16.5453 2.49078V2.49091C16.4999 2.37787 16.3906 2.30355 16.2687 2.30274H12.1232V0.739008C12.1225 0.574009 11.9888 0.440483 11.8238 0.440078L3.25172 0.439941ZM1.94513 2.33782C1.86558 2.33809 1.78937 2.3701 1.73326 2.42648C1.67727 2.483 1.64593 2.55947 1.64633 2.63902C1.64687 2.80376 1.7804 2.93715 1.94513 2.93782H6.47152C6.55107 2.93808 6.62741 2.90688 6.68393 2.85076C6.74045 2.79478 6.77232 2.71857 6.77259 2.63902C6.77299 2.55907 6.74138 2.48219 6.68473 2.42567C6.62822 2.36915 6.55147 2.33755 6.47152 2.33781L1.94513 2.33782ZM12.1235 2.90275H16.065L16.9462 5.08831L12.1233 5.08818L12.1235 2.90275ZM0.592219 4.25085C0.512532 4.25059 0.436058 4.28193 0.379541 4.33818C0.323025 4.39429 0.291149 4.47063 0.291016 4.55032C0.291417 4.62987 0.323292 4.70608 0.379675 4.76206C0.436191 4.81817 0.512666 4.84952 0.592219 4.84911H6.47152C6.55108 4.84952 6.62742 4.81818 6.68393 4.76206C6.74045 4.70608 6.77232 4.62987 6.77259 4.55032C6.77246 4.47063 6.74072 4.39429 6.6842 4.33818C6.62768 4.28193 6.55121 4.25059 6.47152 4.25085H0.592219ZM12.1235 5.68818H17.0905V7.872H16.0604C15.9562 7.15721 15.3455 6.60117 14.6079 6.60117C13.8703 6.60117 13.2581 7.15725 13.1535 7.872H12.1235L12.1235 5.68818ZM6.27232 7.20103C6.27982 7.20089 6.28706 7.20103 6.29456 7.20103C6.77589 7.20103 7.16469 7.59181 7.16469 8.08872C7.16469 8.58558 6.77589 8.97935 6.29456 8.97935C5.81322 8.97935 5.42442 8.58562 5.42442 8.08872C5.42442 7.5996 5.80143 7.21296 6.27231 7.20103H6.27232ZM14.5856 7.20103C14.5931 7.20089 14.6003 7.20103 14.6078 7.20103C15.0891 7.20103 15.4785 7.59181 15.4785 8.08872C15.4785 8.58558 15.0892 8.97935 14.6078 8.97935C14.1265 8.97935 13.7377 8.58562 13.7377 8.08872C13.7377 7.5996 14.1147 7.21296 14.5855 7.20103H14.5856Z"
                                fill="currentColor"
                              />
                            </svg>
                          </span>
                          <span className="flex-1 truncate">Fleets</span>
                          <span className="ml-3 hidden rounded-full border border-solid border-primary-700 bg-primary-700 px-2.5 py-0.5 text-xs font-medium text-black transition-colors duration-150 ease-in-out group-hover:border-primary-600 group-hover:bg-primary-600 md:inline-block">
                            {shipOrders?.fetchOrders.length}
                          </span>
                        </button>
                      </div>
                    </AuthShield>
                    <AuthShield access={["read-salesorders"]}>
                      <div>
                        <button
                          className="group flex w-full items-center rounded-md px-2 py-2 text-left text-sm font-medium leading-6 text-gray-100  transition-colors hover:text-primary-700"
                          type="button"
                          aria-expanded="false"
                          onClick={() => {
                            navigate("/sales/orders");
                          }}
                        >
                          <span
                            className="bi bi-box-seam mr-4 h-6 w-6 flex-shrink-0 text-center text-xl leading-6 text-primary-50 transition-colors group-hover:text-primary-700"
                            aria-hidden="true"
                          />
                          <span className="flex-1 truncate">Orders</span>
                          <span className="ml-3 hidden rounded-full border border-solid border-primary-700 bg-primary-700 px-2.5 py-0.5 text-xs font-medium text-black transition-colors duration-150 ease-in-out group-hover:border-primary-600 group-hover:bg-primary-600 md:inline-block">
                            {openOrders?.fetchOrders.length}
                          </span>
                        </button>
                      </div>
                    </AuthShield>
                  </div>

                  <div className="space-y-1 border-t border-gray-700/50 px-4 py-6">
                    {navigation.map((item) => (
                      <MenuItem
                        key={item.name}
                        name={item.name}
                        icon={item.icon}
                        to={item.path}
                        access={item.access}
                        child={item?.children}
                      />
                    ))}
                  </div>
                  <div className="mt-6 border-0 pt-6 md:mt-auto">
                    <div className="space-y-1 px-4">
                      {secondaryNavigation.map((item) => (
                        <MenuItem
                          key={item.name}
                          name={item.name}
                          icon={item.icon}
                          to={item.path}
                          access={item.access}
                          child={item?.children}
                        />
                      ))}
                    </div>
                  </div>
                </nav>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 flex-shrink-0" aria-hidden="true">
              {/* Dummy element to force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-grow flex-col overflow-y-auto pb-4 pt-5">
          <div className="flex flex-shrink-0 items-center px-6 py-6">
            <Link to="/" className="w-3/4">
              <span className="sr-only">{process.env.REACT_APP_NAME}</span>
              <img src={LogoLight} alt={process.env.REACT_APP_NAME} />
            </Link>
          </div>
          <nav
            className="flex flex-1 flex-col overflow-y-auto"
            aria-label="Sidebar"
          >
            <div className="flex flex-shrink-0 p-6">
              <Link
                to="/settings/account"
                className="group block w-full flex-shrink-0"
              >
                <div className="flex items-center">
                  <div>
                    <Avatar
                      firstName={currentUser?.firstName}
                      lastName={currentUser?.lastName}
                      className="h-9 w-9 bg-primary-700 text-black"
                    />
                  </div>
                  <div className="ml-3">
                    <p className="truncate text-sm font-medium text-white">
                      {currentUser?.fullName}
                    </p>
                    <p className="text-xs font-medium text-primary-300 group-hover:text-gray-200">
                      {currentRole?.roleName}
                    </p>
                  </div>
                </div>
              </Link>
            </div>

            <div className="space-y-1 px-4 pb-6">
              <AuthShield access={["read-fleets"]}>
                <div className="space-y-1">
                  <button
                    className="group flex w-full items-center rounded-md px-2 py-2 text-left text-sm font-medium leading-6 text-gray-100  transition-colors hover:text-primary-700"
                    id="headlessui-disclosure-button-:r2:"
                    type="button"
                    aria-expanded="false"
                  >
                    <span
                      className="bi bi-map mr-4 h-6 w-6 flex-shrink-0 text-center text-xl leading-6 text-primary-50 transition-colors group-hover:text-primary-700"
                      aria-hidden="true"
                    />
                    <span className="flex-1 truncate">Track Shipment</span>
                    <span className="ml-3 hidden rounded-full border border-solid border-primary-700 bg-primary-700 px-2.5 py-0.5 text-xs font-medium text-black transition-colors duration-150 ease-in-out group-hover:border-primary-600 group-hover:bg-primary-600 md:inline-block">
                      0
                    </span>
                  </button>
                </div>
              </AuthShield>
              <AuthShield access={["read-fleets"]}>
                <div>
                  <button
                    className="group flex w-full items-center rounded-md px-2 py-2 text-left text-sm font-medium leading-6 text-primary-50 transition-colors hover:text-primary-700"
                    type="button"
                    aria-expanded="false"
                  >
                    <span
                      className="mr-4 h-6 w-6 flex-shrink-0 text-center text-xl leading-6 text-primary-50 transition-colors group-hover:text-primary-700"
                      aria-hidden="true"
                    >
                      <svg
                        width="18"
                        height="10"
                        viewBox="0 0 18 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-full w-full"
                      >
                        <path
                          d="M3.25172 0.439941C3.08699 0.440611 2.9536 0.574005 2.95293 0.738871C2.9536 0.903603 3.08699 1.03699 3.25172 1.03766H11.5228V7.87216H7.74932C7.64472 7.15737 7.03199 6.60119 6.2943 6.60119C5.55675 6.60119 4.94403 7.15727 4.83942 7.87202L3.55279 7.87215V6.57197C3.55305 6.49202 3.52145 6.41514 3.46493 6.35862C3.40841 6.30211 3.33154 6.2705 3.25158 6.27077C3.17203 6.27104 3.09582 6.30304 3.03984 6.35943C2.98372 6.41594 2.95238 6.49242 2.95278 6.57197V8.17335C2.95345 8.33808 3.08685 8.47147 3.25158 8.47214H4.87397C5.04192 9.10574 5.61485 9.5796 6.29429 9.5796C6.97373 9.5796 7.54664 9.10591 7.71461 8.47214H13.1873C13.3552 9.10574 13.9282 9.5796 14.6076 9.5796C15.287 9.5796 15.8588 9.10591 16.0262 8.47214H17.3914C17.5562 8.47147 17.6896 8.33808 17.6902 8.17335V5.38709C17.6902 5.34866 17.6829 5.31035 17.6685 5.2746L16.5453 2.49078V2.49091C16.4999 2.37787 16.3906 2.30355 16.2687 2.30274H12.1232V0.739008C12.1225 0.574009 11.9888 0.440483 11.8238 0.440078L3.25172 0.439941ZM1.94513 2.33782C1.86558 2.33809 1.78937 2.3701 1.73326 2.42648C1.67727 2.483 1.64593 2.55947 1.64633 2.63902C1.64687 2.80376 1.7804 2.93715 1.94513 2.93782H6.47152C6.55107 2.93808 6.62741 2.90688 6.68393 2.85076C6.74045 2.79478 6.77232 2.71857 6.77259 2.63902C6.77299 2.55907 6.74138 2.48219 6.68473 2.42567C6.62822 2.36915 6.55147 2.33755 6.47152 2.33781L1.94513 2.33782ZM12.1235 2.90275H16.065L16.9462 5.08831L12.1233 5.08818L12.1235 2.90275ZM0.592219 4.25085C0.512532 4.25059 0.436058 4.28193 0.379541 4.33818C0.323025 4.39429 0.291149 4.47063 0.291016 4.55032C0.291417 4.62987 0.323292 4.70608 0.379675 4.76206C0.436191 4.81817 0.512666 4.84952 0.592219 4.84911H6.47152C6.55108 4.84952 6.62742 4.81818 6.68393 4.76206C6.74045 4.70608 6.77232 4.62987 6.77259 4.55032C6.77246 4.47063 6.74072 4.39429 6.6842 4.33818C6.62768 4.28193 6.55121 4.25059 6.47152 4.25085H0.592219ZM12.1235 5.68818H17.0905V7.872H16.0604C15.9562 7.15721 15.3455 6.60117 14.6079 6.60117C13.8703 6.60117 13.2581 7.15725 13.1535 7.872H12.1235L12.1235 5.68818ZM6.27232 7.20103C6.27982 7.20089 6.28706 7.20103 6.29456 7.20103C6.77589 7.20103 7.16469 7.59181 7.16469 8.08872C7.16469 8.58558 6.77589 8.97935 6.29456 8.97935C5.81322 8.97935 5.42442 8.58562 5.42442 8.08872C5.42442 7.5996 5.80143 7.21296 6.27231 7.20103H6.27232ZM14.5856 7.20103C14.5931 7.20089 14.6003 7.20103 14.6078 7.20103C15.0891 7.20103 15.4785 7.59181 15.4785 8.08872C15.4785 8.58558 15.0892 8.97935 14.6078 8.97935C14.1265 8.97935 13.7377 8.58562 13.7377 8.08872C13.7377 7.5996 14.1147 7.21296 14.5855 7.20103H14.5856Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    <span className="flex-1 truncate">Fleets</span>
                    <span className="ml-3 hidden rounded-full border border-solid border-primary-700 bg-primary-700 px-2.5 py-0.5 text-xs font-medium text-black transition-colors duration-150 ease-in-out group-hover:border-primary-600 group-hover:bg-primary-600 md:inline-block">
                      {shipOrders?.fetchOrders.length}
                    </span>
                  </button>
                </div>
              </AuthShield>
              <AuthShield access={["read-salesorders"]}>
                <div className="space-y-1">
                  <button
                    className="group flex w-full items-center rounded-md px-2 py-2 text-left text-sm font-medium leading-6 text-gray-100  transition-colors hover:text-primary-700"
                    type="button"
                    aria-expanded="false"
                    onClick={() => {
                      navigate("/sales/orders");
                    }}
                  >
                    <span
                      className="bi bi-box-seam mr-4 h-6 w-6 flex-shrink-0 text-center text-xl leading-6 text-primary-50 transition-colors group-hover:text-primary-700"
                      aria-hidden="true"
                    />
                    <span className="flex-1 truncate">Orders</span>
                    <span className="ml-3 hidden rounded-full border border-solid border-primary-700 bg-primary-700 px-2.5 py-0.5 text-xs font-medium text-black transition-colors duration-150 ease-in-out group-hover:border-primary-600 group-hover:bg-primary-600 md:inline-block">
                      {openOrders?.fetchOrders.length}
                    </span>
                  </button>
                </div>
              </AuthShield>
            </div>

            <div className="space-y-1 border-t border-gray-700/50 px-4 py-6">
              {navigation.map((item) => (
                <MenuItem
                  key={item.name}
                  name={item.name}
                  icon={item.icon}
                  to={item.path}
                  access={item.access}
                  child={item?.children}
                />
              ))}
            </div>
            <div className="mt-6 border-0 pt-6 md:mt-auto">
              <div className="space-y-1 px-4">
                {secondaryNavigation.map((item) => (
                  <MenuItem
                    key={item.name}
                    name={item.name}
                    icon={item.icon}
                    to={item.path}
                    access={item.access}
                    child={item?.children}
                  />
                ))}
              </div>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
